import { io, type Socket } from 'socket.io-client';
import {
  bindAccountRole,
  hideBotSetup,
  operatorCancelElement,
  operatorFormElement,
  prepareNewOperator,
  prepareOperatorEdit,
  readOperatorForm,
  renderOperatorAdmin,
  renderProjectAdmin,
  showBotSetup
} from './admin.js';
import { api } from './api.js';
import { bindSettingsPreview, currentEmbedCode, currentSiteId, fillGlobalTextSettings, fillSettingsForm, fillSettingsProjectSelect, fillSiteSelect, readGlobalTextSettings, readSettingsForm, refreshWidgetPreview } from './settings.js';
import type { Conversation, ConversationDetails, GlobalWidgetTextSettings, Message, OperatorAccount, OperatorProfile, Project, Site } from './types.js';
import type { BotSchedule, BotScopeSchedule } from './types.js';
import { renderBotScheduleAdmin } from './schedules.js';
import { appendMessage, clearConversation, refs, renderConversation, renderConversationList, renderCounts, showDesk, showLoggedOut, toast, updateConversationHeader } from './ui.js';
import {
  bindNotificationControls,
  disableNotificationsForLogout,
  initializeNotifications,
  notifyAboutIncomingMessage
} from './notifications.js';

let conversations: Conversation[] = [];
let sites: Site[] = [];
let projects: Project[] = [];
let activeStatus: Conversation['status'] = 'NEW';
let selectedId: string | null = null;
let selectedConversation: ConversationDetails | null = null;
let socket: Socket | null = null;
let currentOperator: OperatorProfile | null = null;
let operators: OperatorAccount[] = [];
let typingTimer: number | undefined;

void boot();

async function boot(): Promise<void> {
  bindEvents();
  try {
    const result = await api<{ operator: OperatorProfile }>('/api/auth/me');
    await openWorkspace(result.operator);
  } catch {
    showLoggedOut();
  }
}

function bindEvents(): void {
  bindNotificationControls();
  bindAccountRole();
  refs.loginForm.addEventListener('submit', (event) => void handleLogin(event));
  refs.messageForm.addEventListener('submit', (event) => void handleSend(event));
  refs.messageInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      refs.messageForm.requestSubmit();
    }
  });
  refs.messageInput.addEventListener('input', () => {
    refs.messageInput.style.height = 'auto';
    refs.messageInput.style.height = `${Math.min(refs.messageInput.scrollHeight, 120)}px`;
    emitTyping(Boolean(refs.messageInput.value.trim()));
  });
  refs.messageInput.addEventListener('blur', () => emitTyping(false));
  refs.phoneFilter.addEventListener('input', renderList);
  refs.siteFilter.addEventListener('change', renderList);
  refs.resetFilters.addEventListener('click', () => {
    refs.phoneFilter.value = '';
    refs.siteFilter.value = '';
    renderList();
    refs.phoneFilter.focus();
  });
  document.getElementById('logout-button')?.addEventListener('click', () => void logout());
  document.getElementById('close-button')?.addEventListener('click', () => void setConversationStatus('CLOSED'));
  document.getElementById('reopen-button')?.addEventListener('click', () => void setConversationStatus('OPEN'));
  refs.joinButton.addEventListener('click', () => void joinSelectedConversation());
  refs.visitorEditButton.addEventListener('click', openVisitorProfile);
  refs.visitorProfileShortcut.addEventListener('click', openVisitorProfile);
  visitorProfileCloseButton().addEventListener('click', closeVisitorProfile);
  visitorProfileCancelButton().addEventListener('click', closeVisitorProfile);
  visitorProfileForm().addEventListener('submit', (event) => void saveVisitorProfile(event));
  document.getElementById('back-button')?.addEventListener('click', () => {
    emitTyping(false);
    selectedId = null;
    selectedConversation = null;
    clearConversation();
    renderList();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-status]').forEach((button) => {
    button.addEventListener('click', () => {
      activeStatus = button.dataset.status as Conversation['status'];
      document.querySelectorAll('[data-status]').forEach((tab) => tab.classList.toggle('is-active', tab === button));
      renderList();
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.view;
      if (view === 'settings' || view === 'global-settings' || view === 'faq' || view === 'sites' || view === 'operators' || view === 'bot-schedules') switchView(view);
      else switchView('inbox');
    });
  });
  const settingsForm = document.getElementById('settings-form') as HTMLFormElement;
  settingsForm.addEventListener('submit', (event) => void saveSettings(event));
  (document.getElementById('global-text-settings-form') as HTMLFormElement)
    .addEventListener('submit', (event) => void saveGlobalTextSettings(event));
  bindSettingsPreview((id) => {
    const site = sites.find((item) => item.id === id);
    if (site) fillSettingsForm(site);
  });
  document.getElementById('copy-code')?.addEventListener('click', () => {
    void navigator.clipboard.writeText(currentEmbedCode()).then(() => toast('Код подключения скопирован'));
  });
  (document.getElementById('site-create-form') as HTMLFormElement).addEventListener('submit', (event) => void createSite(event));
  (document.getElementById('project-create-form') as HTMLFormElement).addEventListener('submit', (event) => void createProject(event));
  operatorFormElement().addEventListener('submit', (event) => void saveOperator(event));
  operatorCancelElement().addEventListener('click', () => prepareNewOperator(projects, sites));
}

async function handleLogin(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  refs.loginError.textContent = '';
  const data = new FormData(refs.loginForm);
  try {
    const result = await api<{ operator: OperatorProfile }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: data.get('email'), password: data.get('password') })
    });
    await openWorkspace(result.operator);
  } catch (error) {
    refs.loginError.textContent = error instanceof Error ? error.message : 'Не удалось войти';
  }
}

async function openWorkspace(profile: OperatorProfile): Promise<void> {
  currentOperator = profile;
  const [conversationResult, siteResult, projectResult, operatorResult, globalTextResult] = await Promise.all([
    api<{ conversations: Conversation[] }>('/api/operator/conversations'),
    api<{ sites: Site[] }>('/api/operator/sites'),
    api<{ projects: Project[] }>('/api/operator/projects'),
    profile.role === 'admin'
      ? api<{ operators: OperatorAccount[] }>('/api/operator/operators')
      : Promise.resolve({ operators: [] }),
    profile.role === 'admin'
      ? api<{ settings: GlobalWidgetTextSettings }>('/api/operator/widget-text-settings')
      : Promise.resolve({ settings: null })
  ]);
  conversations = conversationResult.conversations;
  sites = siteResult.sites;
  projects = projectResult.projects;
  operators = operatorResult.operators;
  applyProfile(profile);
  showDesk();
  fillConversationSiteFilter();
  renderList();
  if (profile.role === 'admin') {
    fillGlobalTextSettings(globalTextResult.settings!);
    fillSettingsProjectSelect(projects);
    fillSiteSelect(sites);
    fillProjectSelect();
    prepareNewOperator(projects, sites);
    renderAdminPanels();
  }
  connectRealtime();
  await initializeNotifications();
  await openConversationFromUrl();
}

function connectRealtime(): void {
  socket?.disconnect();
  socket = io({ transports: ['websocket', 'polling'] });
  socket.on('connect', () => refs.connection.classList.add('is-online'));
  socket.on('disconnect', () => refs.connection.classList.remove('is-online'));
  socket.on('conversation:upsert', (conversation: Conversation) => {
    upsertConversation(conversation);
    if (selectedId === conversation.id) {
      selectedConversation = selectedConversation ? { ...selectedConversation, ...conversation } : null;
      updateConversationHeader(conversation, currentOperator!.id);
    }
  });
  socket.on('message:new', ({ message, conversation }: { message: Message; conversation: Conversation }) => {
    upsertConversation(conversation);
    if (selectedId === conversation.id) {
      selectedConversation = selectedConversation ? { ...selectedConversation, ...conversation } : null;
      updateConversationHeader(conversation, currentOperator!.id);
      appendMessage(message);
      if (selectedConversation && !selectedConversation.messages.some((item) => item.id === message.id)) {
        selectedConversation.messages.push(message);
      }
      if (message.senderType === 'visitor') void markRead(conversation.id);
    }
    notifyAboutIncomingMessage(message, conversation);
  });
  socket.on('visitor:presence', ({ visitorId, online }: { visitorId: string; online: boolean }) => {
    const match = conversations.find((item) => item.visitorId === visitorId);
    if (!match) return;
    match.visitorOnline = online;
    if (selectedId === match.id) updateConversationHeader(match, currentOperator!.id);
    renderList();
  });
  socket.on('visitor:updated', ({ visitorId }: { visitorId: string }) => {
    const match = conversations.find((item) => item.visitorId === visitorId);
    if (match && selectedId === match.id) void selectConversation(match.id);
  });
  socket.on('access:updated', () => location.reload());
}

function upsertConversation(conversation: Conversation): void {
  const index = conversations.findIndex((item) => item.id === conversation.id);
  if (index >= 0) conversations[index] = conversation;
  else conversations.push(conversation);
  conversations.sort((a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt));
  renderList();
}

function renderList(): void {
  const phoneQueries = phoneSearchVariants(refs.phoneFilter.value);
  const siteId = refs.siteFilter.value;
  const filtered = conversations.filter((conversation) => {
    if (siteId && conversation.siteId !== siteId) return false;
    if (phoneQueries.length === 0) return true;
    const phone = digitsOnly(conversation.visitorPhone ?? '');
    return Boolean(phone) && phoneQueries.some((query) => phone.includes(query));
  });
  const hasFilters = phoneQueries.length > 0 || Boolean(siteId);
  refs.resetFilters.classList.toggle('is-hidden', !hasFilters);
  renderCounts(filtered);
  renderConversationList(
    filtered.filter((item) => item.status === activeStatus),
    selectedId,
    (id) => void selectConversation(id),
    hasFilters
      ? 'По заданным фильтрам лиды не найдены. Измените условия или сбросьте поиск.'
      : undefined
  );
}

function fillConversationSiteFilter(): void {
  refs.siteFilter.replaceChildren(new Option('Все сайты', ''));
  for (const site of sites) {
    const label = site.projectName ? `${site.projectName} · ${site.name}` : site.name;
    refs.siteFilter.add(new Option(label, site.id));
  }
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function phoneSearchVariants(value: string): string[] {
  const digits = digitsOnly(value);
  if (!digits) return [];
  const variants = new Set([digits]);
  if (digits.length >= 4 && digits.startsWith('8')) variants.add(`7${digits.slice(1)}`);
  if (digits.length >= 4 && digits.startsWith('7')) variants.add(`8${digits.slice(1)}`);
  return [...variants];
}

async function selectConversation(id: string): Promise<void> {
  if (selectedId && selectedId !== id) emitTyping(false);
  selectedId = id;
  renderList();
  try {
    const result = await api<{ conversation: ConversationDetails }>(`/api/operator/conversations/${id}`);
    if (selectedId !== id) return;
    selectedConversation = result.conversation;
    renderConversation(result.conversation, currentOperator!.id);
    await markRead(id);
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось открыть диалог');
  }
}

async function markRead(id: string): Promise<void> {
  await api(`/api/operator/conversations/${id}/read`, { method: 'POST' });
}

async function handleSend(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const text = refs.messageInput.value.trim();
  if (!text || !selectedId) return;
  refs.messageInput.value = '';
  refs.messageInput.style.height = 'auto';
  emitTyping(false);
  try {
    const result = await api<{ message: Message; conversation: Conversation }>(`/api/operator/conversations/${selectedId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text, clientMessageId: crypto.randomUUID() })
    });
    appendMessage(result.message);
    upsertConversation(result.conversation);
    selectedConversation = selectedConversation ? { ...selectedConversation, ...result.conversation } : null;
    updateConversationHeader(result.conversation, currentOperator!.id);
  } catch (error) {
    refs.messageInput.value = text;
    toast(error instanceof Error ? error.message : 'Сообщение не отправлено');
  }
}

function emitTyping(typing: boolean): void {
  if (typingTimer !== undefined) window.clearTimeout(typingTimer);
  typingTimer = undefined;
  if (!selectedId || !socket?.connected) return;
  socket.emit('operator:typing', { conversationId: selectedId, typing });
  if (typing) {
    typingTimer = window.setTimeout(() => {
      if (selectedId && socket?.connected) {
        socket.emit('operator:typing', { conversationId: selectedId, typing: false });
      }
      typingTimer = undefined;
    }, 1_800);
  }
}

async function setConversationStatus(status: Conversation['status']): Promise<void> {
  if (!selectedId) return;
  try {
    const result = await api<{ conversation: Conversation }>(`/api/operator/conversations/${selectedId}`, {
      method: 'PATCH', body: JSON.stringify({ status })
    });
    upsertConversation(result.conversation);
    updateConversationHeader(result.conversation, currentOperator!.id);
    toast(status === 'CLOSED' ? 'Диалог закрыт' : 'Диалог возвращён в работу');
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Статус не изменён');
  }
}

async function joinSelectedConversation(): Promise<void> {
  if (!selectedId) return;
  try {
    const result = await api<{ conversation: Conversation }>(`/api/operator/conversations/${selectedId}/join`, {
      method: 'POST'
    });
    upsertConversation(result.conversation);
    selectedConversation = selectedConversation ? { ...selectedConversation, ...result.conversation } : null;
    updateConversationHeader(result.conversation, currentOperator!.id);
    toast('Теперь диалог ведёте вы. Автоответы бота остановлены.');
    refs.messageInput.focus({ preventScroll: true });
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось вступить в диалог');
  }
}

function openVisitorProfile(): void {
  if (!selectedConversation) return;
  const form = visitorProfileForm();
  const name = form.elements.namedItem('name') as HTMLInputElement;
  const phone = form.elements.namedItem('phone') as HTMLInputElement;
  name.value = selectedConversation.visitor?.displayName ?? '';
  phone.value = selectedConversation.visitor?.phone ?? '';
  visitorProfileStatus().textContent = '';
  visitorProfileDialog().showModal();
  window.setTimeout(() => (name.value ? phone : name).focus({ preventScroll: true }), 0);
}

function closeVisitorProfile(): void {
  visitorProfileStatus().textContent = '';
  visitorProfileDialog().close();
}

async function saveVisitorProfile(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!selectedId || !selectedConversation) return;
  const conversationId = selectedId;
  const form = visitorProfileForm();
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
  const data = new FormData(form);
  submit.disabled = true;
  visitorProfileStatus().textContent = '';
  try {
    const result = await api<{ conversation: ConversationDetails; webhookQueued: boolean }>(
      `/api/operator/conversations/${conversationId}/visitor`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name: data.get('name'), phone: data.get('phone') })
      }
    );
    if (selectedId === conversationId) {
      selectedConversation = result.conversation;
      upsertConversation(result.conversation);
      renderConversation(result.conversation, currentOperator!.id);
    }
    visitorProfileDialog().close();
    toast(result.webhookQueued
      ? 'Карточка сохранена, обращение отправляется в webhook'
      : 'Карточка клиента сохранена');
  } catch (error) {
    visitorProfileStatus().textContent = error instanceof Error ? error.message : 'Не удалось сохранить карточку';
  } finally {
    submit.disabled = false;
  }
}

function visitorProfileDialog(): HTMLDialogElement {
  return document.getElementById('visitor-profile-dialog') as HTMLDialogElement;
}

function visitorProfileForm(): HTMLFormElement {
  return document.getElementById('visitor-profile-form') as HTMLFormElement;
}

function visitorProfileStatus(): HTMLElement {
  return document.getElementById('visitor-profile-status') as HTMLElement;
}

function visitorProfileCloseButton(): HTMLButtonElement {
  return document.getElementById('visitor-profile-close') as HTMLButtonElement;
}

function visitorProfileCancelButton(): HTMLButtonElement {
  return document.getElementById('visitor-profile-cancel') as HTMLButtonElement;
}

async function saveSettings(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const status = document.getElementById('save-status') as HTMLElement;
  status.textContent = 'Сохраняем…';
  try {
    const result = await api<{ site: Site }>(`/api/operator/sites/${currentSiteId()}`, {
      method: 'PATCH', body: JSON.stringify(readSettingsForm())
    });
    sites = sites.map((site) => site.id === result.site.id ? result.site : site);
    fillSiteSelect(sites, result.site.id);
    renderAdminPanels();
    status.textContent = 'Изменения сохранены и отправлены в виджет.';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Не удалось сохранить';
  }
}

async function saveGlobalTextSettings(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const status = document.getElementById('global-text-save-status') as HTMLElement;
  status.textContent = 'Сохраняем общие тексты…';
  try {
    const result = await api<{ settings: GlobalWidgetTextSettings }>('/api/operator/widget-text-settings', {
      method: 'PUT', body: JSON.stringify(readGlobalTextSettings())
    });
    fillGlobalTextSettings(result.settings);
    refreshWidgetPreview();
    status.textContent = 'Глобальные тексты сохранены и отправлены отмеченным сайтам.';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Не удалось сохранить глобальные тексты';
  }
}

async function setSiteGlobalTexts(siteId: string, enabled: boolean): Promise<boolean> {
  try {
    const result = await api<{ site: Site }>(`/api/operator/sites/${siteId}`, {
      method: 'PATCH', body: JSON.stringify({ useGlobalWidgetTexts: enabled })
    });
    sites = sites.map((site) => site.id === result.site.id ? result.site : site);
    if (currentSiteId() === result.site.id) fillSettingsForm(result.site);
    renderAdminPanels();
    refreshWidgetPreview();
    toast(enabled ? 'Для сайта включены глобальные тексты' : 'Для сайта включены индивидуальные тексты');
    return true;
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось изменить режим текстов');
    return false;
  }
}

function switchView(view: 'inbox' | 'settings' | 'global-settings' | 'faq' | 'sites' | 'operators' | 'bot-schedules'): void {
  if (['settings', 'global-settings', 'sites', 'operators', 'bot-schedules'].includes(view) && currentOperator?.role !== 'admin') return;
  refs.inbox.classList.toggle('is-hidden', view !== 'inbox');
  document.getElementById('settings-view')?.classList.toggle('is-hidden', view !== 'settings');
  document.getElementById('global-settings-view')?.classList.toggle('is-hidden', view !== 'global-settings');
  document.getElementById('faq-view')?.classList.toggle('is-hidden', view !== 'faq');
  document.getElementById('sites-view')?.classList.toggle('is-hidden', view !== 'sites');
  document.getElementById('operators-view')?.classList.toggle('is-hidden', view !== 'operators');
  document.getElementById('bot-schedules-view')?.classList.toggle('is-hidden', view !== 'bot-schedules');
  document.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.classList.toggle('is-active', button.dataset.view === view);
  });
}

async function saveBotSchedules(botId: string, input: {
  defaultSchedule: BotSchedule;
  scopeSchedules: BotScopeSchedule[];
}): Promise<void> {
  const result = await api<{ bot: OperatorAccount }>(`/api/operator/bots/${botId}/schedules`, {
    method: 'PUT', body: JSON.stringify(input)
  });
  operators = operators.map((operator) => operator.id === botId ? result.bot : operator);
  renderAdminPanels();
  toast('Расписания бота сохранены');
}

async function createSite(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const status = document.getElementById('site-form-status') as HTMLElement;
  const data = new FormData(form);
  status.textContent = 'Добавляем чат и проверяем страницу…';
  try {
    const result = await api<{ site: Site }>('/api/operator/sites', {
      method: 'POST',
      body: JSON.stringify({
        projectId: data.get('projectId'),
        name: data.get('name'),
        websiteUrl: data.get('websiteUrl')
      })
    });
    sites.push(result.site);
    form.reset();
    fillProjectSelect(result.site.projectId ?? undefined);
    fillSiteSelect(sites, result.site.id);
    prepareNewOperator(projects, sites);
    renderAdminPanels();
    status.textContent = result.site.verificationMessage ?? 'Сайт добавлен';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Чат не добавлен';
  }
}

async function createProject(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const status = document.getElementById('project-form-status') as HTMLElement;
  const data = new FormData(form);
  status.textContent = 'Создаём проект…';
  try {
    const result = await api<{ project: Project }>('/api/operator/projects', {
      method: 'POST',
      body: JSON.stringify({ name: data.get('name'), webhookUrl: data.get('webhookUrl') })
    });
    projects.push(result.project);
    form.reset();
    fillSettingsProjectSelect(projects);
    fillProjectSelect(result.project.id);
    prepareNewOperator(projects, sites);
    renderAdminPanels();
    status.textContent = 'Проект создан. Код подключения готов.';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Проект не создан';
  }
}

async function saveManagedProject(id: string, input: { name: string; webhookUrl: string }): Promise<void> {
  try {
    const result = await api<{ project: Project }>(`/api/operator/projects/${id}`, {
      method: 'PATCH', body: JSON.stringify(input)
    });
    projects = projects.map((project) => project.id === id ? result.project : project);
    fillSettingsProjectSelect(projects);
    fillProjectSelect(id);
    prepareNewOperator(projects, sites);
    renderAdminPanels();
    toast('Проект сохранён');
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось сохранить проект');
  }
}

async function uploadProjectLegalDocument(
  projectId: string,
  documentType: 'personal-data' | 'privacy-policy',
  file: File
): Promise<boolean> {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    toast('Выберите PDF-файл');
    return false;
  }
  if (file.size === 0 || file.size > 10 * 1024 * 1024) {
    toast('Размер PDF должен быть не больше 10 МБ');
    return false;
  }
  try {
    const result = await api<{ project: Project }>(
      `/api/operator/projects/${projectId}/legal-documents/${documentType}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/pdf', 'X-File-Name': encodeURIComponent(file.name) },
        body: file
      }
    );
    projects = projects.map((project) => project.id === projectId ? result.project : project);
    renderAdminPanels();
    toast('Документ загружен. Публичная ссылка готова.');
    return true;
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось загрузить документ');
    return false;
  }
}

async function deleteProjectLegalDocument(
  projectId: string,
  documentType: 'personal-data' | 'privacy-policy'
): Promise<boolean> {
  try {
    const result = await api<{ project: Project }>(
      `/api/operator/projects/${projectId}/legal-documents/${documentType}`,
      { method: 'DELETE' }
    );
    projects = projects.map((project) => project.id === projectId ? result.project : project);
    renderAdminPanels();
    toast('Документ удалён');
    return true;
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось удалить документ');
    return false;
  }
}

async function verifySite(id: string): Promise<void> {
  const status = document.getElementById('site-form-status') as HTMLElement;
  status.textContent = 'Проверяем страницу сайта…';
  try {
    const result = await api<{ site: Site }>(`/api/operator/sites/${id}/verify`, { method: 'POST' });
    sites = sites.map((site) => site.id === id ? result.site : site);
    renderAdminPanels();
    status.textContent = result.site.verificationMessage ?? 'Проверка завершена';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Не удалось проверить сайт';
  }
}

async function saveOperator(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const status = document.getElementById('operator-form-status') as HTMLElement;
  const input = readOperatorForm();
  status.textContent = input.id ? 'Сохраняем доступ…' : input.role === 'bot' ? 'Создаём бота…' : 'Создаём аккаунт…';
  try {
    let account: OperatorAccount;
    if (input.role === 'bot') {
      const result = await api<{ bot: OperatorAccount; setup?: { webhookUrl: string; webhookSecret: string } }>(
        input.id ? `/api/operator/bots/${input.id}` : '/api/operator/bots',
        {
          method: input.id ? 'PATCH' : 'POST',
          body: JSON.stringify({
            displayName: input.displayName,
            enabled: input.enabled,
            projectIds: input.projectIds,
            siteIds: input.siteIds,
            ...(input.channelToken ? { channelToken: input.channelToken } : {})
          })
        }
      );
      account = result.bot;
      if (result.setup) showBotSetup(result.setup);
    } else {
      const result = await api<{ operator: OperatorAccount }>(
        input.id ? `/api/operator/operators/${input.id}` : '/api/operator/operators',
        {
          method: input.id ? 'PATCH' : 'POST',
          body: JSON.stringify({
            displayName: input.displayName,
            email: input.email,
            enabled: input.enabled,
            projectIds: input.projectIds,
            siteIds: input.siteIds,
            ...(input.password ? { password: input.password } : {})
          })
        }
      );
      account = result.operator;
      hideBotSetup();
    }
    const index = operators.findIndex((operator) => operator.id === account.id);
    if (index >= 0) operators[index] = account;
    else operators.push(account);
    prepareNewOperator(projects, sites);
    renderAdminPanels();
    status.textContent = input.id ? 'Доступ обновлён' : input.role === 'bot' ? 'Бот создан' : 'Аккаунт создан';
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : 'Не удалось сохранить аккаунт';
  }
}

async function rotateBotWebhookSecret(operator: OperatorAccount): Promise<void> {
  if (!window.confirm(`Сменить webhook-секрет для «${operator.displayName}»? Старый секрет сразу перестанет работать.`)) return;
  try {
    const result = await api<{ setup: { webhookUrl: string; webhookSecret: string } }>(
      `/api/operator/bots/${operator.id}/rotate-webhook-secret`,
      { method: 'POST' }
    );
    showBotSetup(result.setup);
    toast('Webhook-секрет заменён');
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось сменить секрет');
  }
}

async function deleteAccount(account: OperatorAccount): Promise<void> {
  const kind = account.role === 'bot' ? 'бота' : 'оператора';
  if (!window.confirm(`Удалить ${kind} «${account.displayName}» без возможности восстановления? История его сообщений останется в диалогах.`)) return;
  try {
    const route = account.role === 'bot' ? 'bots' : 'operators';
    await api(`/api/operator/${route}/${account.id}`, { method: 'DELETE' });
    operators = operators.filter((item) => item.id !== account.id);
    if (readOperatorForm().id === account.id) prepareNewOperator(projects, sites);
    if (account.role === 'bot') hideBotSetup();
    renderAdminPanels();
    toast(account.role === 'bot' ? 'Бот удалён' : 'Аккаунт оператора удалён');
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Не удалось удалить аккаунт');
  }
}

function renderAdminPanels(): void {
  if (currentOperator?.role !== 'admin') return;
  renderProjectAdmin(projects, sites, {
    verify: (id) => void verifySite(id),
    configure: (id) => {
      const site = sites.find((item) => item.id === id);
      if (!site) return;
      fillSettingsForm(site);
      switchView('settings');
    },
    setGlobalTexts: (id, enabled) => setSiteGlobalTexts(id, enabled),
    saveProject: (id, input) => void saveManagedProject(id, input),
    uploadLegalDocument: uploadProjectLegalDocument,
    deleteLegalDocument: deleteProjectLegalDocument,
    copyEmbed: (code) => void navigator.clipboard.writeText(code).then(() => toast('Код проекта скопирован'))
  });
  renderOperatorAdmin(operators, projects, sites, {
    edit: (operator) => prepareOperatorEdit(operator, projects, sites),
    rotateSecret: (operator) => void rotateBotWebhookSecret(operator),
    delete: (operator) => void deleteAccount(operator)
  });
  renderBotScheduleAdmin(operators, projects, sites, saveBotSchedules);
}

function fillProjectSelect(selectedId?: string): void {
  const select = document.getElementById('chat-project-select') as HTMLSelectElement;
  select.replaceChildren();
  for (const project of projects) {
    const option = document.createElement('option');
    option.value = project.id;
    option.textContent = project.name;
    select.append(option);
  }
  if (selectedId && projects.some((project) => project.id === selectedId)) select.value = selectedId;
}

function applyProfile(profile: OperatorProfile): void {
  document.querySelectorAll<HTMLElement>('.admin-only').forEach((element) => {
    element.classList.toggle('is-hidden', profile.role !== 'admin');
  });
  const railUser = document.getElementById('rail-user') as HTMLElement;
  railUser.textContent = profile.displayName.charAt(0).toUpperCase() || 'О';
  railUser.title = `${profile.displayName} · ${profile.role === 'admin' ? 'Администратор' : 'Оператор'}`;
}

async function logout(): Promise<void> {
  await disableNotificationsForLogout();
  await api('/api/auth/logout', { method: 'POST' });
  socket?.disconnect();
  conversations = [];
  operators = [];
  projects = [];
  currentOperator = null;
  selectedId = null;
  showLoggedOut();
}

async function openConversationFromUrl(): Promise<void> {
  const requestedId = new URLSearchParams(location.search).get('conversation');
  if (!requestedId) return;
  const conversation = conversations.find((item) => item.id === requestedId);
  if (!conversation) return;
  activeStatus = conversation.status;
  document.querySelectorAll<HTMLButtonElement>('[data-status]').forEach((tab) => {
    tab.classList.toggle('is-active', tab.dataset.status === activeStatus);
  });
  switchView('inbox');
  await selectConversation(requestedId);
  history.replaceState(null, '', '/operator');
}
